'use client';

import { useRef, useState, useTransition } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

type Decision = 'approve' | 'remove';

/**
 * Moderation controls on a design's own page, for admins. The review queue opens designs in a new tab to
 * see them full size, and there was nothing to act with once there. Uses the same audit-logged
 * /api/admin action as the queue.
 *
 * Every decision here is a confirmation step: this box sits right under the design, a single stray click
 * used to make a design public, and an admin is often looking at their OWN design (said so when it is).
 * Removing takes an optional reason, which the author sees. "Keep" on a live reported design clears its
 * reports without counting as a new decision.
 */
export default function AdminModeration({ id, status, reportCount, isOwn }: { id: string; status: string; reportCount: number; isOwn: boolean }) {
  const router = useRouter();
  const [refreshing, startRefresh] = useTransition();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<Decision | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const approveRef = useRef<HTMLButtonElement>(null);
  const removeRef = useRef<HTMLButtonElement>(null);
  const working = busy || refreshing;

  const openConfirm = (decision: Decision) => {
    setError(null);
    setReason('');
    setConfirming(decision);
  };

  const cancel = () => {
    const back = confirming === 'approve' ? approveRef : removeRef;
    setConfirming(null);
    setReason('');
    requestAnimationFrame(() => back.current?.focus());
  };

  const act = async (decision: Decision) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'moderateDesign', id, decision, reason: decision === 'remove' ? reason : undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!data.success) throw new Error(data.error || 'That action failed.');
      setConfirming(null);
      setReason('');
      // Kept "working" until the refreshed page arrives, so the old buttons cannot be pressed again meanwhile.
      startRefresh(() => router.refresh());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That action failed.');
    } finally {
      setBusy(false);
    }
  };

  const removeLabel = status === 'pending' ? 'Reject' : 'Remove';
  const approveLabel = status === 'removed' ? 'Restore' : status === 'approved' ? 'Keep and clear reports' : 'Approve';
  const showApprove = status !== 'approved' || reportCount > 0;

  return (
    <section aria-label="Moderation" className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-zinc-300">
          <span className="font-semibold text-violet-300">Moderation</span> · status <span className="text-white">{status}</span> ·{' '}
          {reportCount} report{reportCount === 1 ? '' : 's'}
          {isOwn && <span className="text-zinc-400"> · your own design</span>}
        </p>
        <Link href="/admin?tab=community" className="text-xs text-violet-300 hover:text-violet-200">Open the review queue →</Link>
      </div>

      {confirming === 'approve' && (
        <div className="space-y-2">
          <p className="text-sm text-zinc-300">
            {status === 'approved'
              ? 'Keep this design live and clear its reports?'
              : `${approveLabel} this design and make it public in the gallery?`}
            {isOwn && ' This is your own design.'}
          </p>
          <div className="flex gap-2">
            <button type="button" autoFocus onClick={() => act('approve')} disabled={working} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-emerald-950 disabled:opacity-60">
              {working ? 'Working…' : `Confirm ${approveLabel.toLowerCase()}`}
            </button>
            <button type="button" onClick={cancel} className="px-3 py-1.5 rounded-lg text-sm text-zinc-400 hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      {confirming === 'remove' && (
        <div className="space-y-2">
          <label htmlFor="moderation-reason" className="block text-xs text-zinc-400">Reason shown to the author (optional)</label>
          <textarea
            id="moderation-reason"
            value={reason}
            maxLength={300}
            rows={2}
            autoFocus
            onChange={e => setReason(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-violet-500"
          />
          <div className="flex gap-2">
            <button type="button" onClick={() => act('remove')} disabled={working} className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-600/90 hover:bg-red-600 text-white disabled:opacity-60">
              {working ? 'Working…' : `Confirm ${removeLabel.toLowerCase()}`}
            </button>
            <button type="button" onClick={cancel} className="px-3 py-1.5 rounded-lg text-sm text-zinc-400 hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      {confirming === null && (
        <div className="flex flex-wrap gap-2">
          {showApprove && (
            <button ref={approveRef} type="button" onClick={() => openConfirm('approve')} disabled={working} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-emerald-950 disabled:opacity-60">
              {approveLabel}
            </button>
          )}
          {status !== 'removed' && (
            <button ref={removeRef} type="button" onClick={() => openConfirm('remove')} disabled={working} className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-600/90 hover:bg-red-600 text-white disabled:opacity-60">
              {removeLabel}
            </button>
          )}
        </div>
      )}
      {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
    </section>
  );
}
